import { useCallback, useEffect, useState } from "react";
import { useSiteContent, isEnabled } from "@/lib/siteContent";
import { resolveMediaUrl, mediaProxyUrlForced, type MediaRef, mediaUrl } from "@/lib/media";
import Video360Viewer from "@/components/Video360Viewer";
import Photo360Viewer from "@/components/Photo360Viewer";
import ViewerErrorBoundary from "@/components/ViewerErrorBoundary";
import AiBadge from "@/components/AiBadge";
import { useMediaAiFlags } from "@/lib/aiFlags";
import { dispatchViewerClosed } from "@/components/PostViewerCta";
import { X, ChevronLeft, ChevronRight, Play } from "lucide-react";

type GalleryItem = {
    id: string;
    /** Media-ID der zugrundeliegenden Datei (für AI-Flag-Lookup). Optional,
     *  weil Legacy-Tiles ohne Backend-Referenz nur einen path haben. */
    mediaId?: string;
    type: string;
    label: string;
    title: string;
    alt?: string;
    src: string;
    thumb: string;
    poster?: string;
    sphere?: string;
    playOverlay?: boolean;
};

type CmsTile = {
    image?: MediaRef; label?: string;
    id?: string; type?: string; title?: string; alt?: string;
    src?: MediaRef; sphere?: MediaRef; poster?: MediaRef; thumb?: MediaRef;
    playOverlay?: boolean;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;

/**
 * Mediengalerie (CMS-gesteuert).
 *
 * Default-Source: `home.gallery.tiles`. Über den `contentKey`-Prop kann
 * die Komponente auch andere Section-Keys wie `sylt.gallery` rendern —
 * daher generisch, aber der Dateiname bleibt aus historischen Gründen
 * `HomeGallery.tsx`.
 *
 * KEIN statischer Fallback, KEINE Legacy-Konstanten — leeres Array
 * rendert einen Empty-State.
 */
interface HomeGalleryProps {
    contentKey?: string;
    testIdPrefix?: string;
}

export default function HomeGallery({
    contentKey = "home.gallery",
    testIdPrefix = "home-gallery",
}: HomeGalleryProps = {}) {
    const { getObject } = useSiteContent();
    const homeGallery = getObject<{ tiles?: CmsTile[] }>(contentKey, {});
    const cmsTiles = (homeGallery.tiles || []).filter(isEnabled);

    const [items, setItems] = useState<GalleryItem[]>([]);

    // CMS-Tiles in Gallery-Item-Struktur konvertieren. Bei leerem Array
    // bleibt `items` leer → Empty-State wird gerendert.
    //
    // WICHTIG: Single-Source-of-Truth ist `t.image` (aus dem Admin-UI
    // MediaListField „Galerie-Medien"). Legacy-Felder wie t.src, t.title,
    // t.type, t.thumb, t.poster, t.label stammen aus alten Seed-Daten und
    // werden IGNORIERT — sonst überschreiben sie die User-Uploads mit
    // veralteten Titeln ("Landhaus · …") und Medien.
    useEffect(() => {
        if (cmsTiles.length === 0) {
            setItems([]);
            return;
        }
        let cancelled = false;
        void (async () => {
            const resolved = await Promise.all(cmsTiles.map(async (t, i) => {
                const contentRef = t.image as MediaRef;
                let mediaType = "photo";
                let posterUrl = "";
                let derivedTitle = "";
                // Vollständige Meta-Info aus /info holen — Titel, media_type,
                // Poster. Diese Werte gewinnen gegen die (potentiell veralteten)
                // Repeater-Felder.
                if (contentRef && typeof contentRef === "object" && contentRef.media_id) {
                    try {
                        const res = await fetch(`${BACKEND_URL}/api/media/${contentRef.media_id}/info`);
                        if (res.ok) {
                            const doc = await res.json();
                            const mt = doc?.media_type;
                            if (mt === "photo_360") mediaType = "photo360";
                            else if (mt === "video_360") mediaType = "video360";
                            else if (mt === "video") mediaType = "video";
                            else mediaType = "photo";
                            if (doc?.poster_url) posterUrl = doc.poster_url;
                            derivedTitle =
                                (doc?.title as string) ||
                                (doc?.original_filename as string) ||
                                "";
                        }
                    } catch { /* ignore, use defaults */ }
                }
                const url = resolveMediaUrl(contentRef, "");
                const label =
                    mediaType === "photo360" ? "360° FOTO" :
                    mediaType === "video360" ? "360° VIDEO" :
                    mediaType === "video" ? "VIDEO" : "FOTO";
                // Titel-Priorität: nur `derivedTitle` aus /info. Kein Rückgriff
                // auf t.title (Legacy-Kruft "Landhaus · …").
                const title = derivedTitle || label;
                const isVideo = mediaType === "video" || mediaType === "video360";
                return {
                    id: t.id || `cms-${i}`,
                    mediaId:
                        contentRef && typeof contentRef === "object"
                            ? contentRef.media_id
                            : undefined,
                    type: mediaType,
                    label,
                    title,
                    src: url,
                    // Für Video/360°-Video: NIEMALS die MP4/HLS-URL als
                    // Thumbnail-<img>-Source nutzen (rendert nicht). Immer
                    // das Poster-Bild aus /info bevorzugen.
                    thumb: isVideo ? (posterUrl || url) : (url || posterUrl),
                    poster: posterUrl,
                    // sphere: **erzwungener Same-Origin-Proxy** (CORS-safe).
                    sphere: (() => {
                        if (!contentRef || typeof contentRef !== "object" || !contentRef.media_id) {
                            return posterUrl || url;
                        }
                        return mediaProxyUrlForced(contentRef.media_id);
                    })(),
                    playOverlay: mediaType === "video",
                } as unknown as GalleryItem;
            }));
            if (!cancelled) setItems(resolved);
        })();
        return () => { cancelled = true; };
    }, [JSON.stringify(cmsTiles)]);
    const [openIdx, setOpenIdx] = useState<number | null>(null);

    /** Beim Schließen des Foto/Video-Modals: passenden Post-Viewer-CTA-
     *  Trigger emittieren. `PostViewerCta` (global in App.js) hört
     *  darauf und blendet die Card ein (Session-Guards inklusive). */
    const close = useCallback(() => {
        setOpenIdx((prevIdx) => {
            if (prevIdx !== null) {
                const t = items[prevIdx]?.type;
                if (t === "photo360") dispatchViewerClosed("photo360");
                else if (t === "video360") dispatchViewerClosed("video360");
            }
            return null;
        });
    }, [items]);
    const prev = useCallback(
        () => setOpenIdx((i) => (i === null ? null : (i - 1 + items.length) % items.length)),
        [items.length],
    );
    const next = useCallback(
        () => setOpenIdx((i) => (i === null ? null : (i + 1) % items.length)),
        [items.length],
    );

    useEffect(() => {
        if (openIdx === null) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") close();
            if (e.key === "ArrowLeft") prev();
            if (e.key === "ArrowRight") next();
        };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [openIdx, close, prev, next]);

    const active: GalleryItem | null =
        openIdx !== null ? items[openIdx] : null;
    void mediaUrl;

    // KI-Flags für alle Gallery-Items zentral laden (ein Batch, Modul-Cache).
    const aiFlags = useMediaAiFlags(items.map((it) => it.mediaId));

    return (
        <>
            {items.length === 0 ? (
                <div
                    className="rounded-sm px-6 py-14 text-center"
                    style={{
                        background: "hsl(var(--hf-surface))",
                        border: "1px dashed hsl(var(--hf-line))",
                    }}
                    data-testid={`${testIdPrefix}-empty`}
                >
                    <div className="hf-eyebrow" style={{ color: "hsl(var(--hf-gold-dark))" }}>
                        Galerie
                    </div>
                    <div className="mt-2 font-display text-lg text-hf-ink">
                        Bald verfügbar.
                    </div>
                    <p className="mt-1 text-sm text-hf-muted">
                        Sobald erste Referenzaufnahmen freigegeben sind, erscheinen sie hier automatisch.
                    </p>
                </div>
            ) : (
            <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5"
                data-testid={`${testIdPrefix}-grid`}
            >
                {items.map((item, idx) => (
                    <button
                        key={item.id}
                        type="button"
                        onClick={() => setOpenIdx(idx)}
                        className="group relative aspect-[4/3] overflow-hidden rounded-sm text-left"
                        style={{
                            background: "hsl(var(--hf-surface))",
                            border: "1px solid hsl(var(--hf-line))",
                        }}
                        data-testid={`${testIdPrefix}-item-${item.id}`}
                        aria-label={`${item.title} öffnen`}
                    >
                        <img
                            src={item.thumb}
                            alt={(item as unknown as { alt?: string }).alt || item.title}
                            width={800}
                            height={600}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                        />
                        {/* KI-Badge — dezent oben rechts, konsistent zu
                         *  allen anderen Badges auf der Seite. Wird nur
                         *  gezeigt wenn das zugrundeliegende Medium im
                         *  Admin als KI-generiert markiert wurde. */}
                        <AiBadge
                            show={!!item.mediaId && aiFlags[item.mediaId] === true}
                            testId={`${testIdPrefix}-item-${item.id}-ai-badge`}
                        />
                        {(item as unknown as { playOverlay?: boolean }).playOverlay && (
                            <span
                                aria-hidden="true"
                                className="absolute inset-0 flex items-center justify-center"
                            >
                                <span
                                    className="inline-flex items-center justify-center w-12 h-12 rounded-full backdrop-blur-sm"
                                    style={{
                                        background: "hsl(var(--hf-ink) / 0.55)",
                                        color: "hsl(var(--hf-gold-soft))",
                                        border: "1px solid hsl(var(--hf-gold-soft) / 0.6)",
                                    }}
                                >
                                    <Play size={22} fill="currentColor" />
                                </span>
                            </span>
                        )}
                        <span
                            className="absolute top-3 left-3 text-[10px] tracking-[0.16em] uppercase px-2 py-1 rounded-sm"
                            style={{
                                background: "hsl(var(--hf-ink) / 0.78)",
                                color: "hsl(var(--hf-gold-soft))",
                            }}
                        >
                            {item.label}
                        </span>
                        <span
                            className="absolute bottom-3 left-3 right-3 text-xs md:text-sm text-white font-medium"
                            style={{ textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}
                        >
                            {item.title}
                        </span>
                    </button>
                ))}
            </div>
            )}

            {active && (
                <div
                    className="fixed inset-0 z-[70] flex items-center justify-center"
                    style={{ background: "rgba(14,23,38,0.88)" }}
                    role="dialog"
                    aria-modal="true"
                    aria-label={active.title}
                    data-testid={`${testIdPrefix}-lightbox`}
                    onClick={close}
                >
                    <div
                        className="relative w-full max-w-6xl mx-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between text-white mb-3">
                            <div>
                                <div className="hf-eyebrow" style={{ color: "hsl(var(--hf-gold-soft))" }}>
                                    {active.label}
                                </div>
                                <div className="font-display text-xl md:text-2xl mt-1">
                                    {active.title}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button type="button" onClick={prev} aria-label="Vorheriges" className="hf-viewer-ctrl-btn" data-testid="lightbox-prev">
                                    <ChevronLeft size={20} />
                                </button>
                                <button type="button" onClick={next} aria-label="Nächstes" className="hf-viewer-ctrl-btn" data-testid="lightbox-next">
                                    <ChevronRight size={20} />
                                </button>
                                <button type="button" onClick={close} aria-label="Schließen" className="hf-viewer-ctrl-btn" data-testid="lightbox-close">
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        <div className="rounded overflow-hidden">
                            {active.type === "photo360" ? (
                                <ViewerErrorBoundary testId="lightbox-photo360-fallback">
                                    <Photo360Viewer
                                        src={(active as unknown as { sphere?: string }).sphere || active.src}
                                        testIdPrefix="lightbox-photo360"
                                        mediaId={(active as unknown as { mediaId?: string }).mediaId || active.id}
                                        mediaTitle={active.title}
                                    />
                                </ViewerErrorBoundary>
                            ) : active.type === "video360" ? (
                                <ViewerErrorBoundary testId="lightbox-video360-fallback">
                                    <Video360Viewer
                                        src={active.src}
                                        poster={active.poster}
                                        testIdPrefix="lightbox-video360"
                                        mediaId={(active as unknown as { mediaId?: string }).mediaId || active.id}
                                        mediaTitle={active.title}
                                    />
                                </ViewerErrorBoundary>
                            ) : active.type === "video" ? (
                                <video
                                    src={active.src}
                                    poster={active.poster}
                                    controls
                                    className="w-full max-h-[80vh]"
                                    style={{ background: "hsl(var(--hf-dark))" }}
                                />
                            ) : (
                                <img
                                    src={active.src}
                                    alt={active.title}
                                    className="w-full max-h-[80vh] object-contain"
                                    style={{ background: "hsl(var(--hf-dark))" }}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
